'use client'

import {
  arrow,
  autoUpdate,
  flip,
  inline,
  offset,
  safePolygon,
  shift,
  useDelayGroupContext,
  useDismiss,
  useFloating,
  UseFloatingOptions,
  UseFloatingReturn,
  useFocus,
  useHover,
  useInteractions,
  useRole
} from '@floating-ui/react'
import {
  FC,
  PropsWithChildren,
  RefObject,
  useMemo,
  useRef,
  useState
} from 'react'

import { createRequiredContext } from '../../utils/create-required-context'
import { UseInteractionsReturn } from '../../types'

interface TooltipContextValue extends UseFloatingReturn, UseInteractionsReturn {
  open: boolean
  setOpen(open: boolean): void
  arrowRef: RefObject<SVGSVGElement | null>
}

const [useTooltipContext, TooltipContextProvider] =
  createRequiredContext<TooltipContextValue>('Tooltip')

export interface TooltipProps extends UseFloatingOptions {
  initialOpen?: boolean
  noRest?: boolean
  persistOnHover?: boolean
}

const Tooltip: FC<PropsWithChildren<TooltipProps>> = ({
  children,
  initialOpen = false,
  placement = 'top',
  strategy,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  noRest = false,
  persistOnHover = false
}) => {
  const arrowRef = useRef<SVGSVGElement>(null)

  const { delay, isInstantPhase } = useDelayGroupContext()
  const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen)

  const open = controlledOpen ?? uncontrolledOpen
  const setOpen = setControlledOpen ?? setUncontrolledOpen

  const data = useFloating({
    placement,
    strategy,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(10),
      inline(),
      flip({
        fallbackAxisSideDirection: 'start',
        crossAxis: placement.includes('-'),
        padding: 5
      }),
      shift({ padding: 5 }),
      arrow({
        element: arrowRef,
        padding: 4
      })
    ]
  })

  const { context } = data

  const hover = useHover(context, {
    move: false,
    // we want to check on null and undefined in the same time
    // eslint-disable-next-line no-eq-null
    enabled: controlledOpen == null,
    restMs: isInstantPhase || noRest ? 0 : 150,
    delay,
    handleClose: persistOnHover ? safePolygon() : null
  })
  const focus = useFocus(context, {
    // we want to check on null and undefined in the same time
    // eslint-disable-next-line no-eq-null
    enabled: controlledOpen == null
  })
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'tooltip' })

  const interactions = useInteractions([focus, dismiss, role, hover])

  const value = useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...data,
      arrowRef
    }),
    [open, setOpen, interactions, data]
  )

  return (
    <TooltipContextProvider value={value}>{children}</TooltipContextProvider>
  )
}

export { Tooltip, useTooltipContext }
